import { Controller } from "@hotwired/stimulus"

// Reloads an already-open Notifications screen when its per-user channel reports
// a newly persisted row. The channel also sends the latest id on reconnect, so a
// native web view that was suspended catches up without polling or a pull refresh.
export default class extends Controller {
  static values = { updatesUrl: String, latestId: Number }

  connect() {
    this._nativeVisible = true
    this._onChanged = this._onChanged.bind(this)
    this._onVisibilityChange = this._onVisibilityChange.bind(this)
    this._onNativeVisibilityChange = this._onNativeVisibilityChange.bind(this)
    document.addEventListener("notifications:changed", this._onChanged)
    document.addEventListener("visibilitychange", this._onVisibilityChange)
    document.addEventListener("native:tab-visibility", this._onNativeVisibilityChange)
  }

  disconnect() {
    document.removeEventListener("notifications:changed", this._onChanged)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    document.removeEventListener("native:tab-visibility", this._onNativeVisibilityChange)
    if (this._reloadTimer) window.clearTimeout(this._reloadTimer)
  }

  _onChanged(event) {
    const latestId = parseInt(event.detail?.latest_notification_id, 10) || 0
    if (!latestId || latestId <= this.latestIdValue) return

    this._pendingLatestId = Math.max(this._pendingLatestId || 0, latestId)
    if (!this._isVisible()) return

    this._scheduleRefresh()
  }

  _onVisibilityChange() {
    if (this._isVisible() && this._pendingLatestId > this.latestIdValue) {
      this._scheduleRefresh()
    }
  }

  _onNativeVisibilityChange(event) {
    this._nativeVisible = event.detail?.visible !== false
    this._onVisibilityChange()
  }

  _isVisible() {
    return this._nativeVisible && document.visibilityState === "visible"
  }

  _scheduleRefresh() {
    if (this._refreshing) {
      this._refreshAgain = true
      return
    }
    if (this._reloadTimer) return

    // Matching can persist many notifications in a short burst. Give that burst a
    // full second to settle, then reload once. An in-place Turbo replacement of the
    // page root can leave a newly visible WKWebView black, while a normal reload is
    // the same reliable path used by pull-to-refresh.
    this._reloadTimer = window.setTimeout(() => {
      this._reloadTimer = null
      this._refresh()
    }, 1000)
  }

  async _refresh() {
    if (this._refreshing || !this._isVisible()) return

    this._refreshing = true
    window.location.reload()
  }
}
